import React, { useState, useEffect } from "react"
import { SortableContainer, SortableElement } from "react-sortable-hoc"
import { makeStyles } from "@material-ui/styles"
import { Button, TextField, Box, List, MenuItem, Divider, Icon, Avatar, Tooltip, Grid } from "@material-ui/core"
import arrayMove from "array-move"
import { Add } from "@material-ui/icons"
import {
  useMutationCreateCategory,
  useMutationCreateKnowledge,
  useMutationUpdateKnowledge,
  useQueryCategories,
  KNOWLEDGE_CONTENT_TYPE,
} from "../../data"
import { useFormUtils, toId, useMountEffect, useDraft, mapToIds, multipleSelectChange } from "../../utils"

import {
  KnowledgeContentTypeIcon,
  OutlinedSelect,
  DraftBlockquote,
  LocationOutlinedSelect,
  GroupOutlinedSelect,
} from ".."
import { CreatorActions, CreatorMaster } from "../Creators"
import { KnowledgeArticleCreatorContent } from "./KnowledgeArticleCreatorContent"
import { TextDivider } from "../TextDivider"
import IconPicker from "../IconPicker/IconPicker"
import { RowBox } from "../Boxes"
import { useAuth } from "../../services"

const useStyles = makeStyles((theme) => ({
  avatar: {
    backgroundColor: theme.palette.primary.main,
  },
  addContentTypeButton: {
    height: 55,
    width: ({ xs }) => (xs ? "100%" : 90),
    paddingLeft: ({ xs }) => (xs ? 0 : 20),
    paddingRight: ({ xs }) => (xs ? 0 : 20),
  },
  contentTypeIcon: {
    color: theme.palette.text.light,
  },
  content: {
    border: `1px solid ${theme.palette.shaded.border}`,
    borderRadius: 4,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  actions: {
    padding: `${theme.spacing(1)}px ${theme.spacing(3)}px ${theme.spacing(3)}px`,
    fontSize: 14,
  },
  sortableListHelper: {
    zIndex: 99999,
  },
}))

const initialState = {
  title: "",
  category: "",
  content: [],
  locations: ["all"],
  groups: ["all"],
}

const initialCategoryState = {
  name: "",
  description: "",
  icon: "category",
}

const SortableContent = SortableElement((props) => <KnowledgeArticleCreatorContent {...props} />)

const SortableListContainer = SortableContainer(
  ({ items, onToggleExpanded, onUpdateContent, onUpdateContentValue, onUpdateContentUploads, onRemoveContent }) => (
    <List component="div">
      {items.map((item, index) => (
        <SortableContent
          key={index}
          index={index}
          contentIndex={index}
          content={item}
          onToggleExpanded={onToggleExpanded}
          onUpdateContent={onUpdateContent}
          onUpdateContentValue={onUpdateContentValue}
          onUpdateContentUploads={onUpdateContentUploads}
          onRemoveContent={onRemoveContent}
        />
      ))}
    </List>
  )
)

const KnowledgeArticleCreator = ({ open, onCreated, onClose, edit, isInline }) => {
  const classes = useStyles({ xs: isInline })
  const { isValid } = useFormUtils()
  const { hasPermission, hasFeature } = useAuth()
  const [createKnowledge] = useMutationCreateKnowledge()
  const [updateKnowledge] = useMutationUpdateKnowledge()
  const [createCategory] = useMutationCreateCategory()
  const { data: categoryData } = useQueryCategories()

  const [title, setTitle] = useState(initialState.title)
  const [category, setCategory] = useState(initialState.category)
  const [groups, setGroups] = useState(initialState.groups)
  const [locations, setLocations] = useState(initialState.locations)
  const [content, setContent] = useState(initialState.content)

  const [categoryName, setCategoryName] = useState(initialCategoryState.name)
  const [categoryDescription, setCategoryDescription] = useState(initialCategoryState.description)
  const [categoryIcon, setCategoryIcon] = useState(initialCategoryState.icon)

  const [draft, setDraft, removeDraft, draftUntouched] = useDraft("knowledge_article_creator", null)

  const [loading, setLoading] = useState(false)

  const isEdit = edit != null

  const showDraft = !isEdit && !!draft && draftUntouched

  useMountEffect(() => {
    if (!edit && draft) {
      draft.title && setTitle(draft.title)
      draft.category && setCategory(draft.category)
      draft.content && setContent(draft.content)
      draft.groups && setGroups(draft.groups)
      draft.locations && setLocations(draft.locations)

      draft.categoryName && setCategoryName(draft.categoryName)
      draft.categoryDescription && setCategoryDescription(draft.categoryDescription)
      draft.categoryIcon && setCategoryIcon(draft.categoryIcon)
    }
  })

  useEffect(() => {
    if (edit) {
      setTitle(edit.title)
      setCategory(edit.category.id)
      setGroups(edit.groups.length ? mapToIds(edit.groups) : ["all"])
      setLocations(edit.locations.length ? mapToIds(edit.locations) : ["all"])

      const collapsedContent = edit.content.map((c) => ({
        ...c,
        expanded: false,
      }))
      setContent([...collapsedContent])
    }
  }, [edit, setTitle, setCategory, setContent])

  const handleClose = (event, isCancel = true) => {
    handleResetState()
    onClose && onClose(isCancel)
  }

  const handleCreated = (item) => {
    removeDraft()
    onCreated && onCreated(item)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (formValid()) {
      setLoading(true)
      const cleanContent = content.map(({ type, heading, values, value, uploads }) => ({
        type: type - 0,
        heading,
        values,
        value,
        uploads: uploads ? uploads.map((upload) => toId(upload)) : [],
      }))
      let newCategory = category
      if (category === "new") {
        const result = await createCategory({
          variables: { name: categoryName, description: categoryDescription, icon: categoryIcon },
        })
        newCategory = result.data.createCategory.id
      }
      const variables = {
        title,
        content: cleanContent,
        category: newCategory,
        locations: locations.includes("all") ? [] : mapToIds(locations),
        groups: groups.includes("all") ? [] : mapToIds(groups),
      }
      if (isEdit) {
        await updateKnowledge({ variables: { id: toId(edit), ...variables } })
        setLoading(false)
        handleClose(e, false)
      } else {
        const result = await createKnowledge({ variables })
        setLoading(false)
        handleCreated(result.data.createKnowledge)
      }
    }
  }

  const handleChange = (set, name, newValue) => {
    set(newValue)
    setDraft((prev) => ({
      ...prev,
      [name]: newValue,
    }))
  }

  const handleUpdateDraftContent = (newValue) => {
    if (edit) return
    setDraft((prev) => ({
      ...prev,
      content: newValue,
    }))
  }

  const handleDiscardDraft = () => {
    removeDraft()
    handleResetState()
  }

  const handleResetState = () => {
    setTitle(initialState.title)
    setContent(initialState.content)
    setCategory(initialState.category)
    setCategoryName(initialCategoryState.name)
    setCategoryDescription(initialCategoryState.description)
    setCategoryIcon(initialCategoryState.icon)
    setGroups(initialState.groups)
    setLocations(initialState.locations)
  }

  const handleLocationsChanged = (event) => {
    handleChange(setLocations, "locations", [...multipleSelectChange(locations, event)])
  }

  const handleRegionChange = (regionLocations) => {
    handleChange(setLocations, "locations", [...mapToIds(regionLocations)])
  }

  const handleGroupsChanged = (event) => {
    handleChange(setGroups, "groups", [...multipleSelectChange(groups, event)])
  }

  const formValid = () =>
    (category === "new" ? isValid(categoryName, categoryDescription, categoryIcon) : isValid(category)) &&
    isValid(title, content)

  const handleAddContent = ({ type }) => {
    setContent((state) => {
      state.forEach((s) => (s.expanded = false))
      const result = [...state, { type, heading: "", value: "", values: [], expanded: true }]
      handleUpdateDraftContent(result)
      return result
    })
  }
  const handleRemoveContent = (index) => {
    setContent((state) => {
      state.splice(index, 1)
      const result = [...state]
      handleUpdateDraftContent(result)
      return result
    })
  }

  const handleUpdateContent = (index, propName, value) => {
    if (content[index][propName] !== value) {
      const newContent = [...content]
      newContent[index][propName] = value
      setContent(newContent)
      handleUpdateDraftContent(newContent)
    }
    // if (propName === "name" && index === contents.length - 1) setStepNameValid(value)
  }

  const handleToggleExpanded = (index) => {
    setContent((state) => {
      state[index].expanded = !state[index].expanded
      const result = [...state]
      handleUpdateDraftContent(result)
      return result
    })
  }

  const handleUpdateContentValue = (index, value) => {
    setContent((state) => {
      state[index].value = value
      const result = [...state]
      handleUpdateDraftContent(result)
      return result
    })
  }
  const handleUpdateContentUploads = (index, uploads) => {
    setContent((state) => {
      state[index].uploads = [...uploads]
      const result = [...state]
      handleUpdateDraftContent(result)
      return result
    })
  }

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    setContent((prev) => {
      const next = arrayMove(prev, oldIndex, newIndex)
      const result = [...next]
      handleUpdateDraftContent(result)
      return result
    })
  }

  const AddContentTypeButton = ({ title: tooltipTitle, type, ml = 0.5 }) => (
    <Box ml={ml} flexGrow={isInline ? 1 : 0}>
      <Tooltip title={tooltipTitle}>
        <Button
          variant="contained"
          disableElevation
          className={classes.addContentTypeButton}
          onClick={() => handleAddContent({ type })}
          data-cy={`Button-add-content-${type}`}
        >
          {!isInline && <Add opacity={0.5} />}
          <KnowledgeContentTypeIcon type={type} />
        </Button>
      </Tooltip>
    </Box>
  )

  const isFormValid = formValid()

  const canCreateCategory = hasPermission("category_create")

  const hasLocationsAndGroups = hasFeature("knowledge_access")

  const form = (
    <>
      <DraftBlockquote show={showDraft} subject="Article" onDiscard={handleDiscardDraft} />

      <Box>
        <OutlinedSelect
          label="Category"
          value={category}
          native={false}
          onChange={(event) => handleChange(setCategory, "category", event.target.value)}
          required
          data-cy="OutlinedSelect-category"
        >
          {canCreateCategory && (
            <MenuItem value="new" data-cy="MenuItem-category-new">
              <RowBox>
                <Icon>add</Icon>
                <Box ml={1}>New category...</Box>
              </RowBox>
            </MenuItem>
          )}
          {canCreateCategory && <Divider />}
          {categoryData &&
            categoryData.categories.map(({ id, name, icon }) => (
              <MenuItem key={id} value={id}>
                <RowBox>
                  <Icon className="material-icons-outlined">{icon}</Icon>
                  <Box ml={1}>{name}</Box>
                </RowBox>
              </MenuItem>
            ))}
        </OutlinedSelect>
      </Box>

      {category === "new" && (
        <Box mt={2}>
          <Divider />
          <Box display="flex" flexDirection="row" mt={2} alignItems="center">
            <Box flexGrow={1}>
              <TextField
                variant="outlined"
                fullWidth
                id="name"
                label="New category name"
                name="name"
                value={categoryName}
                onChange={(event) => handleChange(setCategoryName, "categoryName", event.target.value)}
                autoFocus
                required
                data-cy="TextField-category-name"
              />
            </Box>
            <Box>
              <IconPicker
                value={categoryIcon}
                onChange={(value) => handleChange(setCategoryIcon, "categoryIcon", value)}
                selectText="Category icon"
              >
                <Avatar className={classes.avatar}>
                  <Icon className="material-icons-outlined">{categoryIcon}</Icon>
                </Avatar>
              </IconPicker>
            </Box>
          </Box>
          <Box mt={2} mb={2}>
            <TextField
              variant="outlined"
              fullWidth
              label="New category description"
              name="description"
              value={categoryDescription}
              onChange={(event) => handleChange(setCategoryDescription, "categoryDescription", event.target.value)}
              multiline
              minRows={3}
              required
              data-cy="TextField-category-description"
            />
          </Box>
          <Divider />
        </Box>
      )}

      {hasLocationsAndGroups && (
        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <LocationOutlinedSelect
                value={locations}
                onChange={handleLocationsChanged}
                onRegionChange={handleRegionChange}
                multiple
                data-cy="KnowledgeArticleCreator-locations"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <GroupOutlinedSelect
                value={groups}
                onChange={handleGroupsChanged}
                multiple
                data-cy="KnowledgeArticleCreator-groups"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      <Box mt={2}>
        <TextField
          variant="outlined"
          fullWidth
          id="title"
          label="Article title"
          name="title"
          value={title}
          onChange={(event) => handleChange(setTitle, "title", event.target.value)}
          required
          data-cy="TextField-title"
        />
      </Box>

      <SortableListContainer
        items={content}
        onToggleExpanded={handleToggleExpanded}
        onUpdateContent={handleUpdateContent}
        onUpdateContentValue={handleUpdateContentValue}
        onUpdateContentUploads={handleUpdateContentUploads}
        onRemoveContent={handleRemoveContent}
        useDragHandle
        onSortEnd={handleSortEnd}
        helperClass={classes.sortableListHelper}
      />

      <TextDivider>Add content to your article</TextDivider>

      <RowBox my={2} justifyContent="center">
        <AddContentTypeButton type={KNOWLEDGE_CONTENT_TYPE.TEXT} title="Text content" ml={0} />
        <AddContentTypeButton type={KNOWLEDGE_CONTENT_TYPE.IMAGE} title="Image" />
        <AddContentTypeButton type={KNOWLEDGE_CONTENT_TYPE.NOTE} title="Information, important or warning" />
        <AddContentTypeButton type={KNOWLEDGE_CONTENT_TYPE.FILES} title="File attachment" />
        <AddContentTypeButton type={KNOWLEDGE_CONTENT_TYPE.VIDEO} title="Media" />
      </RowBox>

      <CreatorActions
        id="KnowledgeArticleCreator-CreatorActions"
        subject="Article"
        onClose={handleClose}
        onSubmit={handleSubmit}
        disableSubmit={!isFormValid || loading}
        submitLoading={loading}
      />
    </>
  )

  return (
    <CreatorMaster
      id="KnowledgeArticleCreator"
      open={open}
      subject="Article"
      form={form}
      isEdit={isEdit}
      isInline={isInline}
      onClose={handleClose}
    />
  )
}

export { KnowledgeArticleCreator }
